import type { PackageKind } from "./enums.ts";
import { MONTH_SHORT, shiftDateKey } from "./time.ts";

/**
 * Monthly membership with a class cap (Pilates studio shape, M2; migration 0079).
 *
 * A studio sells "8 classes a month" or "unlimited", renewed each month. A membership is a package
 * product of the kind `membership`: `credits` is the cap of a month (0 and `unlimited` for no cap),
 * `priceFils` the price of a month, `validityDays` the length of a month. Each paid month is one
 * purchase (a period) with the cap as its sessions, from `periodStartsAt` to `expiresAt` (00:00 in
 * the business clock, `validityDays` after the first day). A month starts on the day the member
 * buys, not on a day of the calendar, and always costs the full price (#ai-feedback 2026-10-26).
 * So the cap counts what the ledger counts: a confirmed booking, a late cancel and a no-show that
 * lose the session, in a session that starts inside the period.
 *
 * The renewal is request and remind, not an automatic charge (Teamna has no saved cards): a
 * payment request before the renewal day, the reminder ladder of recurring fees, and the member pays
 * in the app, by the payment link or at the desk. Paying it writes the next period. A month that is
 * not paid has no period, so no session of that month can be booked.
 *
 * Pure: the API (the booking check), Desk web and the phone (the Book tab, the client home meter)
 * read the same rule from here.
 */

/** Who may buy a package, by kind, for the analytics property `package_kind`. `intro` is an M1 intro offer. */
export type PackageKindProperty = "pack" | "membership" | "intro";

export function packageKindOf(product: { packageKind?: PackageKind | null; newClientsOnly?: boolean | null }): PackageKindProperty {
  if (product.packageKind === "membership") return "membership";
  return product.newClientsOnly ? "intro" : "pack";
}

export function isMembership(product: { packageKind?: PackageKind | null }): boolean {
  return product.packageKind === "membership";
}

function parts(key: string): [number, number, number] {
  const [year, month, day] = key.split("-").map(Number);
  return [year!, month!, day!];
}

export type MembershipTerms = {
  /** The day the period ends and the next one starts. */
  renewsOn: string;
  /** What this period costs: the price of a month. */
  priceFils: number;
  /** The classes of this period. Null: unlimited. */
  cap: number | null;
  days: number;
};

/**
 * The terms of a month from `startsOn`: `validityDays` days at the full price with the full cap.
 * The next month starts on `renewsOn`. No part price (#ai-feedback 2026-10-26, card #3): a month
 * starts on the day the member buys, so it is never short.
 */
export function membershipTerms(input: {
  startsOn: string;
  validityDays: number;
  priceFils: number;
  /** Null: unlimited. */
  cap: number | null;
}): MembershipTerms {
  return { renewsOn: shiftDateKey(input.startsOn, input.validityDays), priceFils: input.priceFils, cap: input.cap, days: input.validityDays };
}

/** A membership period as the cap rule reads it. Instants are ISO strings or dates. */
export type MembershipPeriodInput = {
  periodStartsAt: string | Date;
  /** The end of the period: the renewal day, 00:00 in the business clock. */
  expiresAt: string | Date;
  unlimited: boolean;
  /** The sessions left of the cap (the ledger balance). Not read for an unlimited period. */
  left: number;
  /** The period is paid. A first month the business sold "to pay later" is not, until it is paid. */
  paid: boolean;
};

/** Why a membership period cannot pay for a session. */
export type MembershipBlock = "outside_period" | "cap_reached" | "unpaid";

const time = (value: string | Date) => (typeof value === "string" ? Date.parse(value) : value.getTime());

/**
 * THE cap rule. A membership period pays for a session when the session starts inside the period,
 * the period is paid, and the cap has a class left (an unlimited period always has). The booking
 * service (`usablePurchases`), the Book tab (`payPicks`) and the client home meter read it.
 * Null: it pays.
 */
export function membershipBlock(period: MembershipPeriodInput, sessionStartsAt: string | Date): MembershipBlock | null {
  const at = time(sessionStartsAt);
  if (at < time(period.periodStartsAt) || at >= time(period.expiresAt)) return "outside_period";
  if (!period.paid) return "unpaid";
  if (!period.unlimited && period.left <= 0) return "cap_reached";
  return null;
}

/**
 * The classes a period used and its cap, from the ledger: the cap with the sessions the business
 * added by hand, less what is left. One formula for a capped and an unlimited period.
 */
export function membershipUse(period: { creditsTotal: number; creditsAdded: number; balance: number; unlimited: boolean }): {
  used: number;
  /** Null: unlimited. */
  cap: number | null;
} {
  const cap = period.creditsTotal + period.creditsAdded;
  return { used: Math.max(0, cap - period.balance), cap: period.unlimited ? null : Math.max(0, cap) };
}

/** "1 Nov" for a `yyyy-MM-dd` key. */
export function membershipDay(key: string): string {
  const [, month, day] = parts(key);
  return `${day} ${MONTH_SHORT[month - 1]}`;
}

/** "3 of 8 used this month", or "Unlimited". */
export function membershipUseLine(input: { used: number; cap: number | null }): string {
  return input.cap === null ? "Unlimited" : `${input.used} of ${input.cap} used this month`;
}

/** "8 sessions" or "Unlimited sessions". */
function capWords(cap: number | null): string {
  return cap === null ? "Unlimited sessions" : `${cap} ${cap === 1 ? "session" : "sessions"}`;
}

/** The line under a membership in a shop or a package list: "8 sessions in 30 days. It renews when the 30 days end". */
export function membershipOfferLine(input: { cap: number | null; days: number }): string {
  return `${capWords(input.cap)} in ${input.days} days. It renews when the ${input.days} days end`;
}

/** The line under a membership in the member's shop (web and phone): "8 sessions in 30 days · membership, renews each period". */
export function membershipShopLine(product: { credits: number; unlimited?: boolean | null; validityDays: number }): string {
  return [`${capWords(product.unlimited ? null : product.credits)} in ${product.validityDays} days`, "membership, renews each period"].join(" · ");
}

/** "1st", "2nd", "15th", "22nd". */
export function ordinalDay(day: number): string {
  const tens = day % 100;
  if (tens >= 11 && tens <= 13) return `${day}th`;
  const unit = day % 10;
  return `${day}${unit === 1 ? "st" : unit === 2 ? "nd" : unit === 3 ? "rd" : "th"}`;
}

/** The help line for a membership: how a renewal works. One text for the owner panel and the shop. */
export const MEMBERSHIP_RENEWAL_HELP =
  "Before a month ends, Teamna asks for the payment of the next month and sends reminders. The client pays in the app, with the payment link or at the desk. If the next month is not paid when it starts, the client cannot book sessions in that month until they pay. Sessions they booked before stay.";

/** The member's sentence for a cancel at the end of the period. */
export function membershipCancelLine(endsOn: string): string {
  return `Your membership ends on ${membershipDay(endsOn)}. It does not renew after that. Sessions you booked before that day stay.`;
}
